<template>
  <picture
    v-if="variant === 'static'"
    component-name="media"
    ref="tpl_root"
    class="overflow-hidden grid grid-cols-1 place-items-start w-full"
    :class="{
      'h-full max-h-full':
        aspectRatio === 'none' || (!useParallax && aspectRatio !== 'none'),
      'aspect-1/1': aspectRatio === '1 / 1',
      'aspect-3/2': aspectRatio === '3 / 2',
      'aspect-16/9': aspectRatio === '16 / 9',
      'aspect-4/5': aspectRatio === '4 / 5',
    }"
  >
    <source
      v-if="finalPortraitSrcset"
      :srcset="finalPortraitSrcset.srcset"
      :sizes="finalPortraitSrcset.sizes ? finalPortraitSrcset.sizes : undefined"
      media="(orientation: portrait)"
      class="contents"
    />

    <img
      class="relative z-[1] top-0 left-0 row-start-1 row-end-2 col-span-full w-full max-w-none max-h-none min-h-0"
      :class="{
        'h-full max-h-full':
          aspectRatio === 'none' || (!useParallax && aspectRatio !== 'none'),
        'aspect-1/1-parallax': useParallax && aspectRatio === '1 / 1',
        'aspect-3/2-parallax': useParallax && aspectRatio === '3 / 2',
        'aspect-16/9-parallax': useParallax && aspectRatio === '16 / 9',
        'aspect-4/5-parallax': useParallax && aspectRatio === '4 / 5',
        'object-cover': fit === 'cover',
        'object-contain': fit === 'contain',
      }"
      :alt="showTitle ? alt : undefined"
      :width="width"
      :height="height"
      :src="src ? src : undefined"
      :srcset="finalSrcset?.srcset ? finalSrcset.srcset : undefined"
      :sizes="finalSrcset?.sizes ? finalSrcset.sizes : undefined"
      :loading="loading"
      :fetchpriority="fetchpriority"
      @mouseover="showTitle = false"
      @mouseleave="showTitle = true"
      :data-speed="aspectRatio !== 'none' ? 'auto' : undefined"
    />

    <div
      class="overflow-hidden empty:hidden relative z-[2] top-0 left-0 row-start-1 row-end-2 col-span-full w-full"
      :class="{
        'h-full max-h-full':
          aspectRatio === 'none' || (!useParallax && aspectRatio !== 'none'),
        'aspect-1/1': aspectRatio === '1 / 1',
        'aspect-3/2': aspectRatio === '3 / 2',
        'aspect-16/9': aspectRatio === '16 / 9',
        'aspect-4/5': aspectRatio === '4 / 5',
      }"
    >
      <slot />
    </div>
  </picture>

  <div
    v-else-if="variant === 'motion'"
    component-name="media"
    ref="tpl_root"
    class="overflow-hidden grid grid-cols-1 place-items-start w-full"
    :class="{
      'h-full max-h-full':
        aspectRatio === 'none' || (!useParallax && aspectRatio !== 'none'),
      'aspect-1/1': aspectRatio === '1 / 1',
      'aspect-3/2': aspectRatio === '3 / 2',
      'aspect-16/9': aspectRatio === '16 / 9',
      'aspect-4/5': aspectRatio === '4 / 5',
    }"
  >
    <video
      ref="tpl_video"
      class="relative top-0 left-0 z-[1] row-start-1 row-end-2 col-span-full w-full h-full max-w-none max-h-none min-h-0"
      :class="{
        'object-cover': fit === 'cover',
        'object-contain': fit === 'contain',
      }"
      disablePictureInPicture
      disableremoteplayback
      x-webkit-airplay="deny"
      controlsList="nodownload nofullscreen noremoteplayback"
      muted
      playsinline
      loop
      :title="showTitle ? alt : undefined"
      :width="width"
      :height="height"
      :preload="preload"
      :fetchpriority="fetchpriority"
      @mouseover="showTitle = false"
      @mouseleave="showTitle = true"
    >
      <source :src="videoSrc" class="contents" />
    </video>

    <div
      class="overflow-hidden empty:hidden relative z-[2] top-0 left-0 row-start-1 row-end-2 col-span-full w-full"
      :class="{
        'h-full':
          aspectRatio === 'none' || (!useParallax && aspectRatio !== 'none'),
        'aspect-1/1': aspectRatio === '1 / 1',
        'aspect-3/2': aspectRatio === '3 / 2',
        'aspect-16/9': aspectRatio === '16 / 9',
        'aspect-4/5': aspectRatio === '4 / 5',
      }"
    >
      <slot />
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  variant: {
    type: String,
    required: false,
    validator: function (value) {
      return ["static", "motion"].indexOf(value) !== -1;
    },
    default: "static",
  },
  fetchpriority: {
    type: String,
    required: false,
    validator: function (value) {
      return ["auto", "high", "low"].indexOf(value) !== -1;
    },
    default: "auto",
  },
  alt: {
    type: String,
    required: true,
  },
  width: {
    type: [Number, String],
    required: false,
  },
  height: {
    type: [Number, String],
    required: false,
  },
  mediaClass: {
    type: String,
    required: false,
  },
  src: {
    type: String,
    required: true,
  },
  srcset: {
    required: false,
  },
  sizes: {
    required: false,
  },
  cdn: {
    type: [String, Object],
    required: false,
    validator: function (value) {
      return typeof value === "string"
        ? ["cloudinary", "none"].indexOf(value) !== -1
        : /^cloudinary$|^none$/.test(value.provider);
    },
  },
  loading: {
    type: String,
    required: false,
    validator: function (value) {
      return ["auto", "lazy", "eager"].indexOf(value) !== -1;
    },
    default: "auto",
  },
  portrait: {
    type: Object,
    required: false,
  },
  aspectRatio: {
    type: String,
    required: false,
    validator: function (value) {
      return (
        ["none", "1 / 1", "3 / 2", "16 / 9", "4 / 5"].indexOf(value) !== -1
      );
    },
    default: "none",
  },
  useParallax: {
    type: Boolean,
    required: false,
    default: false,
  },
  fit: {
    type: String,
    required: false,
    validator: function (value) {
      return ["cover", "contain"].indexOf(value) !== -1;
    },
    default: "cover",
  },
  useCurtain: {
    type: String,
    required: false,
    validator: function (value) {
      return (
        ["none", "top", "right", "bottom", "left", "center"].indexOf(value) !==
        -1
      );
    },
    default: "none",
  },
  scrollerClass: {
    type: String,
    required: false,
  },
});

const { motionReduced } = usePrefersReducedMotion();
const showTitle = ref(true);
const intersecting = ref();

const preload = ref("none");

const tpl_root = templateRef("tpl_root");

const tpl_video = templateRef("tpl_video");

const { fromCloudinary } = useCdn();
const { screens } = useTailwindCss();

const isPortrait = useMediaQuery("(orientation: portrait)");
const { width: windowWidth } = useWindowSize();

const finalSrcset = computed(() => {
  if (!props.sizes) {
    return undefined;
  }

  return props.cdn === "cloudinary"
    ? getSrcset(props.src, props.sizes)
    : {
        base: props.src,
        srcset: props.srcset,
        sizes: props.sizes,
      };
});

const finalPortraitSrcset = computed(() => {
  if (!props.portrait?.src) {
    return undefined;
  }
  return props.cdn === "cloudinary" && (props.portrait?.sizes || props.sizes)
    ? getSrcset(props.portrait?.src, props.portrait?.sizes || props.sizes)
    : {
        srcset: props.portrait.src,
      };
});

function getOrderedScreens() {
  const orderedScreens = [];

  const orderedQueries = [
    "phablet",
    "tablet",
    "laptop",
    "desktop",
    "extdesktop",
  ];

  for (let i = 0; i < orderedQueries.length; i++) {
    orderedScreens.push({
      prop: orderedQueries[i],
      value: parseInt(screens.value[orderedQueries[i]].min.replace("px", "")),
    });
  }

  return orderedScreens;
}

function getSrcset(url, queries) {
  const orderedScreens = getOrderedScreens();
  const srcset = [];
  const sizes = [];

  for (let i = 0; i < orderedScreens.length; i++) {
    if (queries[orderedScreens[i].prop]) {
      srcset.push(
        `${fromCloudinary(url, { width: queries[orderedScreens[i].prop] })} ${
          orderedScreens[i].value
        }w`
      );

      sizes.push(
        `(min-width: ${orderedScreens[i].value}px) ${
          queries[orderedScreens[i].prop]
        }w`
      );
    }
  }

  return {
    base: fromCloudinary(url, { width: props.sizes.base }),
    srcset: srcset.join(", "),
    sizes: sizes.join(", "),
  };
}

const videoSrc = ref(props.src);
const breakpoints = ref();

onMounted(() => {
  if (props.useCurtain != "none") {
    let fromObj = {};

    if (motionReduced.value) {
      fromObj.opacity = 0;
    } else {
      switch (props.useCurtain) {
        case "top":
          fromObj.clipPath = "inset(100% 0% 0% 0%)";
          break;

        case "right":
          fromObj.clipPath = "inset(0% 100% 0% 0%)";
          break;

        case "bottom":
          fromObj.clipPath = "inset(0% 0% 100% 0%)";
          break;

        case "left":
          fromObj.clipPath = "inset(0% 0% 0% 100%)";
          break;

        case "center":
          fromObj.clipPath = "inset(100% 100% 100% 100%)";
          break;
      }
    }

    nextTick(() => {
      let params = {};

      if (props.scrollerClass) {
        const { getScrolltriggerContainer } = useScrolltriggerContainer();
        const scrollParent = getScrolltriggerContainer(
          tpl_root.value,
          props.scrollerClass
        );

        if (scrollParent) params.scroller = scrollParent;
      }

      gsap.fromTo(tpl_root.value.querySelectorAll("img, video")[0], fromObj, {
        scrollTrigger: {
          trigger: tpl_root.value,
          start: "top bottom",
          toggleActions: "play none none reset",
          ...params,
        },
        opacity: 1,
        clipPath: "inset(0% 0% 0% 0%)",
        ease: "expo.inOut",
        duration: 1.2,
      });
    });
  }

  if (props.variant === "motion") {
    if (props.loading === "lazy") preload.value = "none";
    else if (props.loading === "eager") preload.value = "metadata";
    else preload.value = "auto";

    if (props.sizes) {
      breakpoints.value = useBreakpoints(props.sizes);
    }

    setVideoSrc();

    watchThrottled(
      () => [windowWidth.value, isPortrait.value],
      () => {
        setVideoSrc();
      },
      { throttle: 600, trailing: true, leading: false }
    );

    const { stop } = useIntersectionObserver(
      tpl_video,
      ([{ isIntersecting }], observerElement) => {
        intersecting.value = isIntersecting;
        if (isIntersecting) {
          if (tpl_video.value.paused) {
            tpl_video.value.play();
          }
        } else {
          tpl_video.value.pause();
        }
      }
    );
  }
});

function setVideoSrc() {
  let src;
  if (props.variant === "motion") {
    src =
      isPortrait.value && props.portrait?.src ? props.portrait.src : props.src;
    const screens = getOrderedScreens().reverse();
    let widthSrc = 1920;
    for (let i = 0; i < screens.length; i++) {
      if (windowWidth.value >= screens[i].value) {
        widthSrc = props.sizes[screens[i].prop];
        break;
      }
    }

    videoSrc.value = fromCloudinary(src, {
      quality: props.cdn?.quality ? props.cdn.quality : null,
      width: widthSrc,
    });

    nextTick(() => {
      tpl_video.value.load();
      if (intersecting.value) {
        tpl_video.value.play();
      }
    });
  } else {
    videoSrc.value = undefined;
  }
}

// watch(
//   () => isPortrait.value,
//   (value) => {
//     setVideoSrc();
//   }
// );
</script>
